/**
 * useSession — TanStack Query hook around GET /auth/me.
 *
 * Drives the auth shell: returns 'loading' on first probe,
 * 'authenticated' if /auth/me responds 200, 'unauthenticated' on 401.
 * Other failure modes surface as 'error' so the UI can offer a retry
 * affordance instead of a redirect loop.
 *
 * The full response shape is intentionally typed loosely here —
 * Phase 1 features that need specific fields (org list, permissions)
 * will register `/auth/me` as a critical endpoint with a Zod schema
 * via `@sparta/api-client`'s `registerCriticalEndpoint`.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api-client.js';

export type SessionStatus =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'error';

export interface SessionState {
  status: SessionStatus;
  user: unknown | null;
  refetch: () => void;
}

export function useSession(): SessionState {
  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const result = await apiClient.GET('/auth/me');
      if (result.response.status === 401) {
        return { unauthenticated: true } as const;
      }
      if (!result.response.ok) {
        throw new Error(`/auth/me failed with status ${result.response.status}`);
      }
      return { user: result.data } as const;
    },
    retry: false,
    staleTime: 60_000,
  });

  if (query.isPending) {
    return { status: 'loading', user: null, refetch: () => query.refetch() };
  }
  if (query.isError) {
    return { status: 'error', user: null, refetch: () => query.refetch() };
  }
  if ('unauthenticated' in query.data) {
    return { status: 'unauthenticated', user: null, refetch: () => query.refetch() };
  }
  return { status: 'authenticated', user: query.data.user, refetch: () => query.refetch() };
}
